// src/components/User/UserTable.jsx
// Component to display user data in a table format

import React from 'react';
import RoleBasedComponent from '../common/RoleBasedComponent';

/**
 * UserTable Component
 * Displays a list of users in a table format with edit actions
 * 
 * @param {Array} users - Array of user objects to display
 * @param {Function} onEdit - Callback function when edit button is clicked
 * @param {Function} onDelete - Callback function when delete button is clicked
 * @param {boolean} isLoading - Whether data is currently loading
 */
const UserTable = ({ users, onEdit, onDelete, isLoading }) => {
  
  /**
   * Handle edit button click
   * @param {Object} user - The user item to edit
   */
  const handleEdit = (user) => {
    // Call the parent's edit function
    if (onEdit) {
      onEdit(user);
    }
  };

  /**
   * Handle delete button click
   * @param {Object} user - The user item to delete
   */
  const handleDelete = (user) => {
    if (onDelete) {
      onDelete(user);
    }
  };

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <div className="user-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Employee ID</th>
              <th>Active/Inactive</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="7" className="user-loading">
                Loading user data...
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Show message when no data is available
  if (!users || users.length === 0) {
    return (
      <div className="inventory-data-table-container">
        <table className="inventory-data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Employee ID</th>
              <th>Active/Inactive</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="7" className="user-empty-state">
                No users match the current filter.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Render the table with data
  return (
    <div className="inventory-data-table-container">
      <table className="inventory-data-table">
        {/* Table Header */}
        <thead>
          <tr>
            <th>ID</th>
            <th>Username</th>
            <th>Email</th>
            <th>Role</th>
            <th>Employee ID</th>
            <th>Active/Inactive</th>
            <th>Actions</th>
          </tr>
        </thead>
        
        {/* Table Body - Loop through each user */}
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              {/* Display user ID */}
              <td>{user.id}</td>
              
              {/* Display username */}
              <td>{user.username}</td>
              
              {/* Display email */}
              <td>{user.email}</td>
              
              {/* Display role */}
              <td>{user.role}</td>
              
              {/* Display employee ID */}
              <td>{user.employeeId || '-'}</td>
              
              {/* Display active/inactive status */}
              <td>
                <span className={`category-status-badge ${
                  user.isActive ? 'category-status-active' : 'category-status-inactive'
                }`}>
                  {user.isActive ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      Active
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px' }}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                      Inactive
                    </>
                  )}
                </span>
              </td>
                      
              {/* Action buttons */}
              <td>
                <button 
                  className="user-btn user-btn-action user-btn-edit"
                  onClick={() => handleEdit(user)}
                  title="Edit this user"
                >
                  Edit
                </button>
                <RoleBasedComponent allowedRoles={['superadmin']}>
                  <button
                    className="user-btn user-btn-action user-btn-delete"
                    onClick={() => handleDelete(user)}
                    title="Delete this user"
                    style={{ marginLeft: '8px' }}
                  >
                    Delete
                  </button>
                </RoleBasedComponent>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserTable;